/* eslint-disable camelcase */
import { merge } from 'lodash-es';
import { newCancelTokenFn } from 'hooks/useCancelToken';
import type { GenericAbortSignal } from 'axios';
import { conversationClient, doorClient, ImResponse, leyanbotClient, resolve } from '.';
import { getInfoFromKnowledgeQueryExtra } from 'utils/getItemInfo';

export interface PageParams {
  page_no: number | string;
  page_size: number | string;
}

export interface PageResponse {
  pageSize: number;
  pageNo: number;
  totalCount: number;
  hasNext?: boolean;
}

export type TradeListParams = PageParams &
  Util.RequireAtLeastOne<
    {
      /**
       * 我们自己的买家id
       */
      internal_buyer_id: number | string;
      external_buyer_id: number | string;
      /**
       * 我们自己的店铺id
       */
      internal_store_id: number | string;
      /** 根据订单号查询订单信息。多个以英文逗号间隔。非必填 */

      internal_trade_ids?: string;

      /** 根据平台(虾皮/lazada)订单号查询订单信息。多个以英文逗号间隔。非必填 */

      external_trade_ids?: string;
      /** tokopedia 发票号 */
      invoice_number?: string;
      /**
       * 订单状态 不传默认查询这个买家的所有状态订单
       */
      trade_status?: Omit<Conversation.Trade.Status, 'IN_CANCELING'>;
      /**
       *  是否过滤已取消的退款单，不填时后端当作false处理
       */
      filter_canceled_refund?: boolean;
      /** 通道帐号id, 类似店铺id */
      tunnelAccountId?: string;
    },
    'internal_buyer_id' | 'external_buyer_id'
  >;

export interface TradeListResponse {
  pageInfo: PageResponse;
  tradeList?: Conversation.Trade.Vo[];
  // 强制刷新 @王旭东
  refreshImmediately?: boolean;
}

/**
 * 提取消息体中的 extra 信息
 */
export const transformKnowledgeQueryMessage = (
  knowledgeQueryMessage?: Conversation.KnowledgeQueryMessage,
) => {
  if (knowledgeQueryMessage?.extra) {
    const info = getInfoFromKnowledgeQueryExtra(knowledgeQueryMessage.extra);
    knowledgeQueryMessage.extraInfo = info?.extraInfo;
    knowledgeQueryMessage.itemInfo = info?.itemInfo;
    knowledgeQueryMessage.rewriteQuery = info?.rewriteQuery;
  }
};

/**
 *
 * @param params 前端将params.filter_canceled_refund 默认设置为true
 * @returns
 */
export function tradeList(
  _params: TradeListParams,
  newCancelToken?: newCancelTokenFn,
  signal?: GenericAbortSignal,
) {
  const params: TradeListParams = { filter_canceled_refund: true, ..._params };

  return resolve(
    doorClient.get<ImResponse<TradeListResponse>>('/aggregation/v1/trade/search', {
      params: merge(params, { refreshImmediately: true, needHistory: true }),
      cancelToken: newCancelToken?.(),
      signal,
    }),
  );
}

export function getTradeEligibilityList(
  body: { internalTradeIds: string[] },
  signal?: GenericAbortSignal,
) {
  return resolve(
    doorClient.post<
      ImResponse<{
        orderEligibilityList: Conversation.Trade.Eligibility[];
      }>
    >('/trade/v1/reverse/eligibility', body, { signal }),
  );
}

export interface CalculateRes {
  currency: string;
  refundTotal: string;
  refundSubtotal: string;
  refundShippingFee: string;
  refundTax: string;
  retailDeliveryFee: string;
  modifyTheAmount: boolean;
}

export function calculateCurrency(body: {
  internalTradeId: string;
  requestType: Conversation.Trade.EligibilityType;
  shipmentType?: 'PLATFORM' | 'BUYER_ARRANGE';
  handoverMethod?: 'DROP_OFF' | 'PICKUP';
  reasonName: string;
  orderLineList: Conversation.Trade.ReverseEligibilityOrderLine[];
}) {
  return resolve(doorClient.post<ImResponse<CalculateRes>>('/trade/v1/refund/calculate', body));
}

export function createCancel(body: {
  internalTradeId: string;
  orderLineList: Conversation.Trade.ReverseEligibilityOrderLine[];
  cancelReason: string;
}) {
  return doorClient.post('/trade/v1/cancel/create', body);
}

export function createReturn(body: {
  internalTradeId: string;
  orderLineList: Conversation.Trade.ReverseEligibilityOrderLine[];
  returnReason: string;
  requestType: 'REFUND' | 'RETURN_AND_REFUND';
  currency: string;
  refundTotal: string;
  shipmentType?: 'PLATFORM' | 'BUYER_ARRANGE';
  handoverMethod?: 'DROP_OFF' | 'PICKUP';
}) {
  return doorClient.post('/trade/v1/return/create', body);
}

export type LogisticsListParams =
  | {
      /**
       * 订单id（我们的数据库id）,多个以英文逗号间隔。（一次最多传入10个订单ID）
       */
      internal_trade_ids: string;
      /**
       * 不传默认为false。是否不从缓存中获取（缓存10分钟），传true则实时获取
       */
      no_cache?: false;
      needReverse?: boolean;
    }
  | {
      internalStoreId: number | string;
      externalTradeIds: string;
      needPackageData: boolean;
    };

export interface LogisticsListResponse {
  data: Conversation.Trade.Logistics[];
  thirdTrackingInfo?: Conversation.Trade.ThirdLogistics[];
  reverseData?: Conversation.Trade.ReverseLogistics[];
}

export function logisticsList(params: LogisticsListParams, signal?: GenericAbortSignal) {
  return doorClient.get<LogisticsListResponse>('/trade/v1/tracking/info', {
    params,
    signal,
  });
}

export function lazadaLogisticsList(params: LogisticsListParams, signal?: GenericAbortSignal) {
  return doorClient.get<{
    data: Conversation.Trade.LazadaLogistics[];
    thirdTrackingInfo?: Conversation.Trade.ThirdLogistics[];
  }>('/trade/v1/lazada/tracking/info', {
    params,
    signal,
  });
}

/** 上报物流翻译 */
export function trackingInfoTransReport<T extends General.PlatformUpperCaseTunnel>(
  params: Conversation.Trade.TrackingInfoTransReportReq<T>,
) {
  return resolve(
    doorClient.post<ImResponse<undefined>>('/trade/v1/trackingInfoTransReport', params),
  );
}

export type ReturnReasonTransReportParams =
  | Conversation.Trade.ReturnReasonTransReportData
  | Conversation.Trade.ReturnReasonTransReportData[];

/** 上报退款翻译 */
export function returnReasonTransReport(params: ReturnReasonTransReportParams) {
  const data = Array.isArray(params) ? params : [params];
  return doorClient
    .post<ImResponse<boolean>>('/trade/v1/returnReasonTransReport', { data })
    .then(() => true)
    .catch(() => {
      return false;
    });
}

interface ReviewTransReport {
  /**
   * 评论
   */
  id: string;
  /**
   * 1: 是主评论的翻译, 2: 是附属评论的翻译,
   */
  flag: 1 | 2;
  transComment: Review.Translation;
}
export type ReviewTransReportBody = ReviewTransReport | ReviewTransReport[];
export function reviewTransReport(body: ReviewTransReportBody) {
  const transReportList = Array.isArray(body) ? body : [body];
  return doorClient
    .post(
      '/oversea-item/v1/trans/report/new',
      Array.isArray(body)
        ? {
            transReportList,
          }
        : body,
    )
    .then(() => true)
    .catch(() => false);
}

interface ICommonTransReport {
  table: 'TRADE';
  field: 'BUYER_MEMOS';
  /** 内部表主键 上报备注等传订单接口中返回的 internalTradeId */
  key: string;
  originText: string;
  translation: Record<string, string>;
}
export type CommonTransReportParams = ICommonTransReport | ICommonTransReport[];
export function commonTransReport(params: CommonTransReportParams) {
  const data = Array.isArray(params) ? params : [params];
  return doorClient
    .post<ImResponse<undefined>>('/trade/v1/commonTransReport', {
      data,
    })
    .then(() => true)
    .catch(() => false);
}

export type UpdateTradeCommentBody = Pick<
  Conversation.Trade.Vo,
  'internalTradeId' | 'sellerMemos' | 'editSellerMemoType'
> & {
  platform: General.PlatformUpperCaseTunnel;
};

export function updateTradeComment(body: UpdateTradeCommentBody) {
  return resolve(
    doorClient.put<ImResponse<UpdateTradeCommentBody>>('/trade/v1/trade/seller_memos', body),
  );
}

/** 本地搜索: 根据订单查询店铺id和买家id */
export type SearchStoreBuyerParam = { externalTradeId: string };
export type SearchStoreBuyerDataRsp = {
  /** 我们的买家id */
  internalBuyerId: string;
  /** 平台的买家id */
  externalBuyerId: string;
  /** 平台的店铺id */
  internalShopId: string;
  /** 平台的店铺id */
  externalShopId: string;
  /** 平台 */
  platform: string;
};

export function searchStoreBuyer(params: SearchStoreBuyerParam) {
  return resolve(
    doorClient.get<ImResponse<SearchStoreBuyerDataRsp>>('/trade/v1/trade/store_buyer', { params }),
  );
}

export enum ProductListParamsKeywordType {
  ITEM_NAME = 0,
  ITEM_ID = 1,
  SKU = 2,
  ITEM_NO = 3,
}

export interface ProductListParams extends PageParams {
  /**
   * 我们自己的卖家店铺id
   */
  internal_store_id: number | string;
  /**
   * 关键词搜索，不传为查全部，根据商品名称模糊搜索
   */
  keyword?: string;
  /**
   * 排序字段 目前有 SALE (销量)和 UPDATE_TIME（更新时间）
   */
  order_by?: 'SALE' | 'UPDATE_TIME';
  /**
   * 默认为false。 是否需要库存字段，如果传true，则接口响应时间会很长（spu的库存数相对来说不是很重要）
   */
  need_stock?: boolean;
  /**
   * 内部商品id 多个以英文逗号隔开
   */
  internal_item_ids?: string;
  /**
   * 外部商品id 多个以英文逗号隔开
   */
  external_item_ids?: string;
  /**
   * 内部的买家id 用来查询最近买家咨询
   */
  internal_buyer_id?: string;
  /**
   * 商品状态，不传查 NORMAL
   */
  itemStatus?: Conversation.Product['itemStatus'] | 'ALL';
  /**
   * 搜索类型
   * @default ProductListParamsKeywordType.ITEM_NAME
   */
  keyword_type?: ProductListParamsKeywordType;
  /**
   * 默认为false，是否需要库存规则信息，这里只有回话侧边栏的场景调用的时候传true
   */
  needStockRule?: boolean;
  /**
   * SHIPPING_WAREHOUSE 发货仓库
   * REISSUE_WAREHOUSE 补发仓库
   */
  warehouseType?: 'SHIPPING_WAREHOUSE' | 'REISSUE_WAREHOUSE';
  ttsToko?: boolean;
  /** 同类目优先 */
  sortStrategy?: 'CATEGORY_PRIORITY' | string;
}

export interface ProductListResponse {
  pageInfo: PageResponse;
  itemList?: Conversation.Product[];
}

export function productList(params: ProductListParams, signal?: GenericAbortSignal) {
  return resolve(
    doorClient.get<ImResponse<ProductListResponse>>('/item/v2/items/search', {
      params: { ...params, compact: true },
      signal,
    }),
  );
}

export function getRecentConsultingItem(params: ProductListParams, signal?: GenericAbortSignal) {
  return resolve(
    doorClient.get<ImResponse<ProductListResponse>>('/item/v2/getRecentConsultingItem', {
      params: { ...params, compact: true },
      signal,
    }),
  );
}

export interface ProductListForMessageParams {
  storeId: string;
  itemId: string;
}

export interface ProductListForMessageResponse {
  items?: Conversation.Product[];
}

// 此接口会在数据库没有数据的情况下，向官方请求数据
export function productListForMessage(params: ProductListForMessageParams) {
  return doorClient.get<ProductListForMessageResponse>('/item/batchGetItemInfo', {
    params,
  });
}

export function itemSkuGet(externalSkuId: string, signal?: GenericAbortSignal) {
  return resolve(
    doorClient.get<ImResponse<Conversation.ProductSku>>('/item/sku/get', {
      params: { externalSkuId },
      signal,
    }),
  );
}

export interface ItemCardProductParams {
  storeId: string;
  externalItemId: string;
  needStockRule?: boolean;
}

// 商品卡片专用接口。后端说别的地方不能用这个接口，原因未知。@weizhi.zhao
export function itemCardProduct(params: ItemCardProductParams, signal?: GenericAbortSignal) {
  return resolve(
    doorClient.get<ImResponse<Conversation.MessageProduct | undefined>>('/item/getItemInfo', {
      params,
      signal,
    }),
  );
}

export interface VoucherListParams {
  /**
   * 店铺id（我们的数据库id）
   */
  internal_store_id: number | string;
  /**
   * 优惠券状态  不传默认查全部（进行中和未开始的）
   */
  voucher_status?: Conversation.Voucher.Status;
  /**
   * 优惠券搜索关键字
   */
  searchKeyword?: string;
  keywordType?: Conversation.Voucher.SearchKeywordType;
}

export interface VoucherListResponse<T> extends ImResponse<T> {
  lastSyncTime: string;
}

export function voucherList(params: VoucherListParams) {
  return doorClient.get<VoucherListResponse<Conversation.Voucher.Vo[] | undefined>>(
    '/oversea-item/v1/voucher/list',
    {
      params,
    },
  );
}

export interface VoucherParams {
  internal_store_id: string;
  external_voucher_id: string;
  platform?: General.PlatformUpperCaseTunnel;
}

export function voucher(params: VoucherParams) {
  return resolve(
    doorClient.get<ImResponse<Conversation.Voucher.Vo | undefined>>(
      '/oversea-item/v1/voucher/single',
      {
        params,
      },
    ),
  );
}

export interface VoucherAvailableProductListParams {
  /**
   * 店铺id（我们的数据库id）
   */
  internal_store_id: number | string;
  /**
   * 优惠券的数据库id
   */
  internal_voucher_id: number | string;
  /**
   * 平台优惠券 id
   */
  external_voucher_id: number | string;
}

export function voucherAvailableProductList(params: VoucherAvailableProductListParams) {
  return resolve(
    doorClient.get<ImResponse<Conversation.Voucher.Product[] | undefined>>(
      '/oversea-item/v1/voucher/items',
      {
        params,
      },
    ),
  );
}

export interface SyncVoucherBody {
  internalStoreId: number;
}

export function syncVoucher(body: SyncVoucherBody) {
  return resolve(doorClient.post<ImResponse<undefined>>('/vouchers/sync', body));
}

export interface UpdateVoucherStatusBody {
  internalVoucherId: number;
  type: 'END' | 'SUSPEND' | 'RESUME';
  operatorId: number;
  language: 'english' | 'chinese';
}

export function updateVoucherStatus(body: UpdateVoucherStatusBody) {
  return resolve(doorClient.post<ImResponse<undefined>>('/voucher/status/change', body));
}

type ConversationMessage = Conversation.Message;

export interface QueryConversationParams {
  // store_id: string;
  // assistant_id: string;
  conversation_id: string;
  offset?: string;
  page_size: number;
  /** 0：往小于`sendTime`的方向,向上翻页; 1：往大于`sendTime`的方向,向下翻页，默认0 */
  dir?: number;
  message_type?: ConversationMessage['messageType'];
  /**
   * 是否与 Mercado 对话
   */
  with_mercado?: boolean;
  claimId?: string;
}

export const patchKwaiMsgResource = (message: ConversationMessage) => {
  const { channel, msgId, messageType, imageContent, videoContent } = message;

  if (channel !== 'KWAI') {
    return Promise.resolve();
  }

  if (messageType === 'IMAGE' && imageContent) {
    const { imgUrl, externalImgUrl } = imageContent;
    if (!imgUrl && externalImgUrl.startsWith('ks://')) {
      return getKwaiResourceUrl({ msgId, uri: externalImgUrl })
        .then(({ url }) => {
          imageContent.externalImgUrl = url;
        })
        .catch(() => {});
    }
  }

  if (messageType === 'VIDEO' && videoContent) {
    const { localVideoUrl, videoUrl } = videoContent;
    if (!localVideoUrl && videoUrl.startsWith('ks://')) {
      return getKwaiResourceUrl({ msgId, uri: videoUrl })
        .then(({ url }) => {
          videoContent.videoUrl = url;
        })
        .catch(() => {});
    }
  }

  return Promise.resolve();
};

export interface QueryIntentByMessageIdsResponse {
  success: boolean;
  data: Array<{
    messageId: number;
    storeId?: string;
    llmIntentId?: string;
    llmIntentName?: string;
    llmPolicyId?: string;
    llmPolicyName?: string;
    llmSubPolicyId?: string;
  }>;
  timestamp: number;
}

export const queryIntentByMessageIds = (queryMessageIds: string[]) => {
  return leyanbotClient.post<QueryIntentByMessageIdsResponse>(
    '/beeswax/v1/query_intent_by_message_ids',
    { queryMessageIds },
  );
};

export const queryConversation = (
  params: QueryConversationParams,
  iframeMode?: boolean,
  newCancelToken?: newCancelTokenFn,
) => {
  const url = iframeMode
    ? '/oversea-user-voice/v1/queryConversation'
    : '/aggregation/v1/queryConversation';
  return doorClient
    .get<ImResponse<{ message?: ConversationMessage[]; keyMsgId?: string }>>(url, {
      params,
      cancelToken: newCancelToken?.(),
    })
    .then((res) => {
      res.data.data.message = res.data.data.message?.map((message) => {
        transformKnowledgeQueryMessage(message);
        return message;
      });
      const messageList = res.data.data.message;

      if (!messageList?.length || messageList[0].channel !== 'KWAI') {
        return res;
      }

      return Promise.all(messageList.map(patchKwaiMsgResource)).then(() => res);
    })
    .then((res) => {
      if (!iframeMode) {
        return res;
      }
      const messageIds = res.data.data.message
        ?.filter(({ msgFromType }) => msgFromType === 'BUYER')
        .map(({ msgId }) => msgId);
      if (!messageIds?.length) {
        return res;
      }
      return Promise.all([
        leyanbotClient.post<any>('/beeswax/v1/query_bot_reply_detail', {
          messageIds,
        }),
        leyanbotClient.post<any>('/beeswax/v1/query_llm_processed_detail', {
          messageIds: messageIds.map(Number),
        }),
      ])
        .then(([extra, llmDetail]) => {
          res.data.data.message = res.data.data.message?.map((message) => {
            const aiExtraTarget = extra.data.data.find(
              ({ messageId }: any) => `${messageId}` === message.msgId,
            );
            const llmDetailTarget = llmDetail.data.data.find(
              ({ messageId }: any) => `${messageId}` === message.msgId,
            );
            message.aiExtra = aiExtraTarget;
            message.llmDetail = llmDetailTarget;
            return message;
          });
          return res;
        })
        .catch(() => {
          return res;
        });
    });
};

/** 被踢出群聊后,发消息失败,重新加入群聊 */
export const restartTeam = (params: ApiConversation.RestartTeamParam) => {
  return resolve(conversationClient.post<ImResponse<undefined>>('/restartTeam', params));
};

/** 获取聊天对话框顶部的买家信息 */
export const getConversationTopBarInfo = (
  params: { conversationId: string },
  signal?: GenericAbortSignal,
) => {
  return resolve(
    doorClient
      .get<ImResponse<Conversation.TopBarInfo>>('/aggregation/v1/getConversationTopBarInfo', {
        params,
        signal,
      })
      .then((res) => {
        if (res.data?.data?.knowledgeQuery?.extra) {
          const info = getInfoFromKnowledgeQueryExtra(res.data.data.knowledgeQuery.extra);
          res.data.data.knowledgeQuery.itemInfo = info?.itemInfo;
          res.data.data.knowledgeQuery.rewriteQuery = info?.rewriteQuery;
        }
        if (res.data?.data?.knowledgeReplies) {
          Object.values(res.data.data.knowledgeReplies).forEach((reply) => {
            if (reply.query.extra) {
              const info = getInfoFromKnowledgeQueryExtra(reply.query.extra);
              reply.query.itemInfo = info?.itemInfo;
              reply.query.rewriteQuery = info?.rewriteQuery;
            }
          });
        }

        return res;
      }),
  );
};

export interface QueryStickerParams {
  region?: General.Region;
  externalType?: General.PlatformUpperCase;
  etag?: string;
}

/**
 *
 * @param params 不传入params时返回所有stickers
 * @returns
 */
export const querySticker = (params?: QueryStickerParams) => {
  return resolve(
    conversationClient.get<ImResponse<{ etag: string; stickerPack: Conversation.Sticker[] }>>(
      '/querySticker',
      {
        params,
      },
    ),
  );
};

export interface RecallMessageParams {
  conversationId: string;
  messageId: string;
  storeId: string;
}

export const recallMessage = (params: RecallMessageParams) => {
  return conversationClient.get<ImResponse<never>>('/messageRecall', {
    params,
  });
};

export interface LazadaVideoUrlParams {
  msgId: string;
  videoId: string;
}

export const lazadaVideoUrl = (params: {
  videoId: string;
  msgId: string;
  conversationId?: string;
}) => {
  return resolve(
    conversationClient.get<ImResponse<{ videoUrl: string }>>('/getLazadaVideo', { params }),
  );
};

export interface GenerateInvoiceImageParams {
  externalTradeId: string | number;
  internalBuyerId: string | number;
  internalStoreId: string | number;
}

export const generateInvoiceImage = (params: GenerateInvoiceImageParams) => {
  return resolve(doorClient.get<ImResponse<string>>('/aggregation/v1/generateInvoice', { params }));
};

export interface InvoiceInfo {
  invoiceNo: string;
  orderNo: string;
  orderDate: string;
  invoiceTo: string;
  invoiceDate: string;
  shippingAddress: string;
  phone: string;
  shopName: string;
  items: {
    productName: string;
    qty: string;
    paidPrice: string;
    paidPriceUnit: string;
  }[];
  subtotal: string;
  discount: string;
  shipping: string;
  others: string;
  netPaid: string;
}

export const getInvoiceInfo = (params: GenerateInvoiceImageParams) => {
  return resolve(
    doorClient.get<ImResponse<InvoiceInfo>>('/aggregation/v1/generateInvoice/param', { params }),
  );
};

export interface LogoutOffline {
  userId: number;
  orgId: number;
}
// 退出登陆时离线
export const logoutOffline = (body: LogoutOffline) => {
  return resolve(
    doorClient.post<ImResponse<undefined>>('/oversea-conversation/v1/logoutNotify', body),
  );
};

export const getShopeeInviteFollowerUrl = (storeId: string | number) => {
  return resolve(
    doorClient.get<
      ImResponse<{
        imgUrl: string;
        externalImgUrl: string;
        width: number;
        height: number;
      }>
    >('/aggregation/v1/follower/imgUrl', {
      params: { storeId },
    }),
  );
};

export function getMercadoAttachment(
  storeId: Conversation.Conversation['storeId'],
  fileName: Conversation.MsgAttachmentInfo['filename'],
  msgId: Conversation.Message['msgId'],
) {
  return resolve(
    doorClient.get<ImResponse<{ url: string }>>('/oversea-conversation/v1/getMercadoAttachment', {
      params: {
        store_id: storeId,
        file_name: fileName,
        msg_id: msgId,
      },
    }),
  );
}
export interface ExchangeProduct {
  subOrderId: string;
  // 上一次换货的 skuid
  oldSkuId?: string;
  newSkuId: string;
  quantity: number;
}

export interface ExchangeItemBody {
  platform: General.Platform;
  externalStoreId: string;
  internalTradeId: string;
  exchangeProdList: ExchangeProduct[];
}

// 换货
export const exchangeItem = (body: ExchangeItemBody) => {
  return resolve(doorClient.post<ImResponse<undefined>>('/trade/v1/exchange/items', body));
};

export interface ExchangeItemLog {
  userName: string;
  operateTimeMs: number;
  exchangeProd: ExchangeProduct;
}
export interface ExchangeItemsLogRes {
  exchangeItemLogList: ExchangeItemLog[];
}

/**
 * 获取换货记录
 */
export const getExchangeItemsLog = (internalTradeId: Conversation.Trade.Vo['internalTradeId']) => {
  return resolve(
    doorClient.get<ImResponse<ExchangeItemsLogRes>>('/trade/v1/exchange/items/log', {
      params: { internalTradeId },
    }),
  );
};

/**
 * shopee同步评价
 */
export const syncOrderReview = (data: { platform: string; orderId: string; spuId: string[] }) => {
  return resolve(
    doorClient.post<ImResponse<undefined>>('/oversea-item/v1/sync_order_reivew', data),
  );
};

export const syncItem = (payload: { internalStoreId: string }) => {
  return resolve(doorClient.post<ImResponse>('/item/v1/sync', payload));
};

export const getSyncItemStatus = (payload: { internalStoreId: string }) => {
  return resolve(
    doorClient.get<ImResponse<{ lastSyncTimeMs: string; isSyncing: boolean }>>('/item/v1/sync', {
      params: payload,
    }),
  );
};

export const getTiktokNormalQualityVideo = (params: { conversationId: string; msgId: string }) => {
  return resolve(
    doorClient.get<ImResponse<Conversation.MsgVideo>>(
      '/oversea-conversation/v1/getTiktokNormalQualityVideo',
      { params },
    ),
  );
};

export const getKwaiResourceUrl = (params: { msgId: string; uri: string }) => {
  return resolve(
    doorClient.get<ImResponse<{ url: string }>>('/oversea-conversation/v1/getKwaiResourceUrl', {
      params,
    }),
  );
};

export interface GetVideoAsrTextBody {
  marketplace: General.PlatformUpperCaseTunnel;
  region: General.UpperCaseRegion;
  storeId: string;
  source: 'message' | 'refund';
  sourceId: string;
  videoPath: string;
}

export const getVideoAsrText = (body: GetVideoAsrTextBody) => {
  return resolve(leyanbotClient.post<ImResponse<{ asrText: string }>>('/asr/post/asr', body));
};

export const getBundleMsg = (params: { conversationId: string; msgId: string; isSbs: boolean }) => {
  return resolve(
    doorClient.get<ImResponse<{ message: Conversation.Message[] }>>(
      '/oversea-conversation/v1/queryBundleMsg',
      { params },
    ),
  );
};

export const bundleMsgTranslateReport = (body: {
  translateReportInfo: {
    msgId: string;
    translation: {
      text: string;
      source: string;
      targetLanguage: string;
    };
  }[];
}) => {
  return doorClient.post('/oversea-conversation/v1/translateReportBundleMsg', body);
};

export const updateUserHangRecover = (action: 'RECOVER' | 'HANG') => {
  return conversationClient.post('/userHangRecover', { action });
};

export const getJolectBuyer = (externalBuyerId: string) => {
  return resolve(
    conversationClient.get<ImResponse<Conversation.JolectBuyer>>('/getJolectBuyer', {
      params: { externalBuyerId },
    }),
  );
};

export const getFacebookBuyer = (externalBuyerId: string) => {
  return resolve(
    conversationClient.get<ImResponse<Conversation.FacebookBuyer>>('/getFacebookBuyer', {
      params: { externalBuyerId },
    }),
  );
};

export const updateFacebookBuyer = (body: {
  externalBuyerId: string;
  contact: string;
  remark: string;
}) => {
  return conversationClient.post('/updateFacebookBuyer', body);
};

export const getFacebookMessage = (params: { storeId: string; msgId: string }) => {
  return resolve(
    conversationClient.get<ImResponse<Pick<Conversation.Message, 'fbMsgContent'>>>(
      '/getFacebookMessage',
      { params },
    ),
  );
};

interface GetKnowledgeQuerySpuGroupParams {
  externalSpuId: string;
  externalStoreId: string;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
}

export interface KnowledgeQuerySpuGroup {
  /** SKU组ID */
  groupId: string;
  /** SKU组哈希键 */
  skuGroupHashKey: string;
  /** SKU键 */
  skuKey: string;
  /** SKU数量 */
  skuCount: number;
  /** 平台列表 */
  platforms: string[];
  /** 创建时间 */
  createTime: {
    date: {
      year: number;
      month: number;
      day: number;
    };
    time: {
      hour: number;
      minute: number;
      second: number;
      nano: number;
    };
  };
  /** 更新时间 */
  updateTime: {
    date: {
      year: number;
      month: number;
      day: number;
    };
    time: {
      hour: number;
      minute: number;
      second: number;
      nano: number;
    };
  };
  /** SKU映射列表 */
  skuMappings: {
    /** 映射ID */
    id: number;
    /** SKU组ID */
    skuGroupId: string;
    /** SKU组哈希键 */
    skuGroupHashKey: string;
    /** 组织ID */
    orgId: string;
    /** 组所有者 */
    groupOwner: string;
    /** SKU键 */
    skuKey: string;
    /** 卖家SKU */
    sellerSku: string;
    /** SPU键 */
    spuKey: string;
    /** SKU排名 */
    skuRank: number;
    /** 店铺ID */
    storeId: string;
    /** 平台 */
    platform: string;
    /** 创建时间 */
    createTime: {
      date: {
        year: number;
        month: number;
        day: number;
      };
      time: {
        hour: number;
        minute: number;
        second: number;
        nano: number;
      };
    };
    /** 更新时间 */
    updateTime: {
      date: {
        year: number;
        month: number;
        day: number;
      };
      time: {
        hour: number;
        minute: number;
        second: number;
        nano: number;
      };
    };
  }[];
}

export const getKnowledgeQuerySpuGroup = (params: GetKnowledgeQuerySpuGroupParams) => {
  return doorClient.get<{ code: number; msg: string; skuGroups: KnowledgeQuerySpuGroup[] }>(
    '/itemCluster/v1/getSkuGroupBySpuKey',
    {
      params: {
        ...params,
        groupOwner: 'product_detail',
      },
    },
  );
};

/**
 * 获取会话的QA Pair信息
 */
export const getConversationKnowledgeQuery = (
  conversationId: string,
  signal?: GenericAbortSignal,
) => {
  return resolve(
    doorClient
      .get<ImResponse<Conversation.ConversationKnowledgeQuery>>(
        '/oversea-conversation/v1/getConversationKnowledgeMark',
        {
          params: {
            conversationId,
          },
          signal,
        },
      )
      .then((res) => {
        transformKnowledgeQueryMessage(res.data.data.knowledgeQuery);
        transformKnowledgeQueryMessage(res.data.data.knowledgeReply);

        Object.values(res.data.data.knowledgeReplies).forEach((reply) => {
          transformKnowledgeQueryMessage(reply.query);
        });
        res.data.data.knowledgeQueries.forEach((query) => {
          transformKnowledgeQueryMessage(query);
        });

        return res;
      }),
  );
};
